import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Pressable,
  ActivityIndicator,
  View,
  useWindowDimensions,
  RefreshControl,
  Platform,
  ScrollView,
} from 'react-native';
import { FlashList } from '@shopify/flash-list';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { router, useLocalSearchParams } from 'expo-router';
import { useScrollToTop, useFocusEffect } from 'expo-router/react-navigation';

import { useIsLargeScreen } from '@/hooks/useIsLargeScreen';
import { useMeasurementFormatters } from '@/components/providers/MeasurementsProvider';
import { useAuth } from '@/components/providers/AuthProvider';
import { Heading } from '@/components/gluestack/heading';
import { Text } from '@/components/gluestack/text';
import { VStack } from '@/components/gluestack/vstack';
import { HStack } from '@/components/gluestack/hstack';
import { Box } from '@/components/gluestack/box';
import { ScreenHeader } from '@/components/layout/ScreenHeader';
import { MessageView } from '@/components/ui/MessageView';
import { ErrorInListView } from '@/components/ui/ErrorInListView';
import { InlineTabsBar, TabItem } from '@/components/ui/InlineTabsBar';
import { HeaderRefreshButton } from '@/components/ui/HeaderRefreshButton';
import { PhotoCard } from '@/components/domain/photos/PhotoCard';
import { EventCard, EventCardSkeleton } from '@/components/domain/events/EventCard';
import { MyTypeFilterMenu } from '~/components/domain/common/MyTypeFilterMenu';
import { DiscoverTabBarHint } from '~/components/domain/discover/DiscoverTabBarHint';
import {
  getFilterVersion,
  registerFilterChangedHandler,
  unregisterFilterChangedHandler,
} from '~/hooks/useInfiniteListUtils';
import { PhotosService } from '~/services/domain/PhotosService';
import { EventsService } from '~/services/domain/EventsService';
import { Photo } from '~/models/Photo';
import { EventBasic } from '~/models/Event';
import { PhotoListType } from '~/constants/lists';
import {
  AlbumNotFound02Icon,
  Calendar03Icon,
  Clock01Icon,
  StarIcon,
  SquareLock01Icon,
  FavouriteIcon,
  Location01Icon,
  UserGroupIcon,
} from '@hugeicons-pro/core-solid-rounded';
import {
  ArrowRight01Icon,
  Calendar03Icon as Calendar03StrokeIcon,
  GridViewIcon,
} from '@hugeicons-pro/core-stroke-rounded';
import { HugeIconsWrapper } from '@/components/ui/HugeIconsWrapper';
import { HugeiconsProps } from '@hugeicons/react-native';

// xl breakpoint is 1280px in Tailwind
const XL_BREAKPOINT = 1280;
const MAX_DISCOVER_PHOTOS = 6;
const DISCOVER_EVENTS_NEARBY_RADIUS_KM = 1000;
const KM_TO_MI = 0.621371;

function formatDiscoverNearbyRadius(km: number, distanceUnit: 'km' | 'mi'): string {
  if (distanceUnit === 'km') {
    const roundedKm = Math.round(km / 100) * 100;
    return `${roundedKm} km`;
  }
  const roundedMi = Math.round((km * KM_TO_MI) / 100) * 100;
  return `${roundedMi} mi`;
}

interface DiscoverSectionHeaderProps {
  icon: HugeiconsProps['icon'];
  title: string;
  subtitle?: string;
  onPress: () => void;
}

const DISCOVER_SECTION_BG_ICON_SIZE = 88;

const DiscoverSectionHeader = ({ icon, title, subtitle, onPress }: DiscoverSectionHeaderProps) => {
  const { t } = useTranslation();

  return (
    <Box className="relative mb-3 min-h-12 overflow-visible">
      <Box
        pointerEvents="none"
        className="absolute -bottom-9 -right-1 z-0 opacity-40"
        accessibilityElementsHidden
        importantForAccessibility="no-hide-descendants">
        <HugeIconsWrapper
          icon={icon}
          size={DISCOVER_SECTION_BG_ICON_SIZE}
          className="color-background-500"
        />
      </Box>
      <Pressable
        onPress={onPress}
        className="z-10 max-w-[85%] self-start pt-3"
        accessibilityRole="button"
        accessibilityLabel={`${title}, ${t('discover.seeMore')}`}>
        <VStack className="gap-0.5">
          <HStack className="items-center gap-1.5">
            <Heading size="md" className="font-extrabold text-tertiary-500" numberOfLines={1}>
              {title}
            </Heading>
            <HugeIconsWrapper
              icon={ArrowRight01Icon}
              size={22}
              className="shrink-0 color-tertiary-500"
            />
          </HStack>
          {subtitle ? (
            <Text
              size="sm"
              className="text-typography-500"
              numberOfLines={2}
              androidBoldTextTrailingSpaces={2}>
              {subtitle}
            </Text>
          ) : null}
        </VStack>
      </Pressable>
    </Box>
  );
};

interface DiscoverSeeAllTileProps {
  onPress: () => void;
}

const DiscoverSeeAllTile = ({ onPress }: DiscoverSeeAllTileProps) => {
  const { t } = useTranslation();

  return (
    <Pressable onPress={onPress} className="flex-1" accessibilityRole="button">
      <Box className="aspect-square w-full items-center justify-center gap-1 rounded-lg bg-background-50">
        <HugeIconsWrapper icon={GridViewIcon} size={28} className="text-typography-500" />
        <Text size="sm" className="mt-2 text-center text-typography-500">
          {t('discover.seeMore')}
        </Text>
      </Box>
    </Pressable>
  );
};

interface DiscoverSeeAllPhotoOverlayProps {
  photo: Photo;
  onPress: () => void;
}

const DiscoverSeeAllPhotoOverlay = ({ photo, onPress }: DiscoverSeeAllPhotoOverlayProps) => {
  const { t } = useTranslation();

  return (
    <View className="flex-1">
      <PhotoCard photo={photo} disableImageViewer />
      <Pressable
        onPress={onPress}
        className="absolute inset-0 items-center justify-center gap-1 rounded-lg bg-primary-600/70 dark:bg-primary-400/75"
        accessibilityRole="button"
        accessibilityLabel={t('discover.seeMore')}>
        <HugeIconsWrapper icon={GridViewIcon} size={28} className="text-white" />
        <Text size="sm" className="text-center font-medium text-white">
          {t('discover.seeMore')}
        </Text>
      </Pressable>
    </View>
  );
};

interface PhotoSectionData {
  key: string;
  titleKey: string;
  subtitleKey: string;
  icon: HugeiconsProps['icon'];
  photos: Photo[];
  route: `/photos/${PhotoListType}`;
}

interface PhotoSectionProps {
  section: PhotoSectionData;
  isLoading: boolean;
  photosPerRow: number;
}

type PhotoGridCell =
  | { type: 'photo'; photo: Photo }
  | { type: 'seeAllTile' }
  | { type: 'seeAllOverlay'; photo: Photo };

const PhotoSection = ({ section, isLoading, photosPerRow }: PhotoSectionProps) => {
  const { t } = useTranslation();
  const carouselPhotosRef = useRef<Photo[]>([]);

  const handleSeeMore = () => {
    router.navigate(section.route);
  };

  const previewPhotos = section.photos.slice(0, MAX_DISCOVER_PHOTOS);
  const useSixthAsSeeAll = previewPhotos.length === MAX_DISCOVER_PHOTOS;
  const gridPhotos = useSixthAsSeeAll
    ? previewPhotos.slice(0, MAX_DISCOVER_PHOTOS - 1)
    : previewPhotos;
  const seeAllOverlayPhoto = useSixthAsSeeAll ? previewPhotos[MAX_DISCOVER_PHOTOS - 1] : undefined;
  const showStandaloneSeeAllTile = previewPhotos.length > 0 && !useSixthAsSeeAll;

  carouselPhotosRef.current = previewPhotos;
  const getPhotosForCarousel = useCallback(() => carouselPhotosRef.current, []);
  const hasPhotos = previewPhotos.length > 0;

  const gridCells: PhotoGridCell[] = useMemo(() => {
    const cells: PhotoGridCell[] = gridPhotos.map((photo) => ({ type: 'photo', photo }));
    if (showStandaloneSeeAllTile) {
      cells.push({ type: 'seeAllTile' });
    } else if (seeAllOverlayPhoto) {
      cells.push({ type: 'seeAllOverlay', photo: seeAllOverlayPhoto });
    }
    return cells;
  }, [gridPhotos, showStandaloneSeeAllTile, seeAllOverlayPhoto]);

  const gridRows: PhotoGridCell[][] = useMemo(() => {
    const rows: PhotoGridCell[][] = [];
    for (let i = 0; i < gridCells.length; i += photosPerRow) {
      rows.push(gridCells.slice(i, i + photosPerRow));
    }
    return rows;
  }, [gridCells, photosPerRow]);

  const skeletonRowCount = Math.ceil(MAX_DISCOVER_PHOTOS / photosPerRow);
  const skeletonRows = Array.from({ length: skeletonRowCount }, (_, i) => i);

  const renderGridCell = (cell: PhotoGridCell) => {
    if (cell.type === 'seeAllTile') {
      return <DiscoverSeeAllTile onPress={handleSeeMore} />;
    }
    if (cell.type === 'seeAllOverlay') {
      return <DiscoverSeeAllPhotoOverlay photo={cell.photo} onPress={handleSeeMore} />;
    }
    return (
      <PhotoCard photo={cell.photo} showLikesComments getPhotosForCarousel={getPhotosForCarousel} />
    );
  };

  return (
    <VStack className="mb-6">
      <DiscoverSectionHeader
        icon={section.icon}
        title={t(section.titleKey)}
        subtitle={t(section.subtitleKey)}
        onPress={handleSeeMore}
      />

      {isLoading ? (
        <VStack className="gap-3 md:gap-4">
          {skeletonRows.map((rowIndex) => (
            <HStack key={`skeleton-row-${rowIndex}`} className="gap-3 md:gap-4">
              {Array.from({ length: photosPerRow }).map((_, colIndex) => (
                <View key={`skeleton-${rowIndex}-${colIndex}`} className="flex-1">
                  <PhotoCard />
                </View>
              ))}
            </HStack>
          ))}
        </VStack>
      ) : !hasPhotos ? (
        <Box className="rounded-lg bg-background-0 py-8">
          <MessageView
            type="info"
            text={t('photos.empty')}
            icon={AlbumNotFound02Icon}
            iconSize={48}
            disableHeight
          />
        </Box>
      ) : (
        <VStack className="gap-3 md:gap-4">
          {gridRows.map((row, rowIndex) => (
            <HStack key={`row-${rowIndex}`} className="gap-3 md:gap-4">
              {row.map((cell, colIndex) => (
                <View
                  key={
                    cell.type === 'photo'
                      ? cell.photo.Id
                      : cell.type === 'seeAllOverlay'
                        ? `see-all-${cell.photo.Id}`
                        : 'see-all-tile'
                  }
                  className="flex-1">
                  {renderGridCell(cell)}
                </View>
              ))}
              {row.length < photosPerRow &&
                Array.from({ length: photosPerRow - row.length }).map((_, emptyIndex) => (
                  <View key={`empty-${emptyIndex}`} className="flex-1" />
                ))}
            </HStack>
          ))}
        </VStack>
      )}
    </VStack>
  );
};

interface EventSectionData {
  key: string;
  titleKey: string;
  subtitleKey: string;
  subtitleParams?: Record<string, string>;
  icon: HugeiconsProps['icon'];
  events: EventBasic[];
  route: string;
}

const EVENT_CARD_GAP = 12;
const EVENT_CARD_PADDING_LEFT = 16;

interface EventSectionProps {
  section: EventSectionData;
  isLoading: boolean;
  cardWidth: number;
}

const EventSection = ({ section, isLoading, cardWidth }: EventSectionProps) => {
  const { t } = useTranslation();

  const handleSeeMore = () => {
    router.navigate(section.route as any);
  };

  const displayEvents = section.events.slice(0, 8);
  const hasEvents = displayEvents.length > 0;

  // alignItems: 'stretch' makes the See All tile fill the full row height
  const scrollContentStyle = {
    paddingHorizontal: EVENT_CARD_PADDING_LEFT,
    gap: EVENT_CARD_GAP,
    alignItems: 'stretch' as const,
  };
  // Negative margin escapes the parent padding so cards scroll edge-to-edge
  const scrollStyle = { marginHorizontal: -EVENT_CARD_PADDING_LEFT };

  return (
    <VStack className="mb-6">
      <DiscoverSectionHeader
        icon={section.icon}
        title={t(section.titleKey)}
        subtitle={t(section.subtitleKey, section.subtitleParams)}
        onPress={handleSeeMore}
      />

      {isLoading ? (
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          style={scrollStyle}
          contentContainerStyle={scrollContentStyle}>
          {Array.from({ length: 4 }).map((_, i) => (
            <View key={i} style={{ width: cardWidth }}>
              <EventCardSkeleton />
            </View>
          ))}
        </ScrollView>
      ) : !hasEvents ? (
        <Box className="rounded-lg bg-background-50 py-8">
          <MessageView
            type="info"
            text={t('events.empty')}
            icon={Calendar03StrokeIcon}
            iconSize={48}
            disableHeight
          />
        </Box>
      ) : (
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          style={scrollStyle}
          contentContainerStyle={scrollContentStyle}>
          {displayEvents.map((event) => (
            <View key={event.Id} style={{ width: cardWidth }}>
              <EventCard event={event} />
            </View>
          ))}
          <Pressable
            onPress={handleSeeMore}
            style={{ width: cardWidth }}
            className="items-center justify-center gap-1 rounded-lg bg-background-50">
            <HugeIconsWrapper icon={GridViewIcon} size={28} className="text-typography-500" />
            <Text size="sm" className="mt-2 text-center text-typography-500">
              {t('discover.seeMore')}
            </Text>
          </Pressable>
        </ScrollView>
      )}
    </VStack>
  );
};

export default function Discover() {
  const { t } = useTranslation();
  const { width } = useWindowDimensions();
  const { currentCommunity } = useAuth();
  const isLargeScreen = useIsLargeScreen();
  const { distanceUnit } = useMeasurementFormatters();
  const nearbyRadiusLabel = useMemo(
    () => formatDiscoverNearbyRadius(DISCOVER_EVENTS_NEARBY_RADIUS_KM, distanceUnit),
    [distanceUnit]
  );
  const queryClient = useQueryClient();
  const { tab } = useLocalSearchParams<{ tab?: string }>();
  const [activeTab, setActiveTab] = useState(tab === 'events' ? 'events' : 'photos');
  const [isRefreshing, setIsRefreshing] = useState(false);

  const handleTabChange = useCallback((newTab: string) => {
    setActiveTab(newTab);
    router.setParams(newTab === 'events' ? { tab: 'events' } : { tab: undefined });
  }, []);

  const flashListRef = useRef<React.ComponentRef<typeof FlashList<PhotoSectionData>>>(null);
  const isFocusedRef = useRef(true);
  const lastFilterVersionRef = useRef(getFilterVersion());

  useFocusEffect(
    useCallback(() => {
      isFocusedRef.current = true;
      return () => {
        isFocusedRef.current = false;
      };
    }, [])
  );

  useEffect(() => {
    const key = 'discover';
    registerFilterChangedHandler(key, () => {
      if (!isFocusedRef.current) return;
      lastFilterVersionRef.current = getFilterVersion();
      queueMicrotask(() => {
        queryClient.resetQueries({ queryKey: ['discovery'] });
      });
    });
    return () => unregisterFilterChangedHandler(key);
  }, [queryClient]);

  useFocusEffect(
    useCallback(() => {
      const current = getFilterVersion();
      if (lastFilterVersionRef.current !== current) {
        lastFilterVersionRef.current = current;
        queryClient.resetQueries({ queryKey: ['discovery'] });
      }
    }, [queryClient])
  );

  // 6 photos per row on xl+ screens, 3 on smaller screens
  const photosPerRow = width >= XL_BREAKPOINT ? 6 : 3;

  // Number of cards fully visible — fractional part creates the peek effect
  const eventsVisibleCount = !isLargeScreen ? 1.7 : width >= 1280 ? 5.3 : width >= 1024 ? 4.3 : 3.3;
  const eventCardWidth =
    (width - EVENT_CARD_PADDING_LEFT - Math.floor(eventsVisibleCount) * EVENT_CARD_GAP) /
    eventsVisibleCount;

  const tabs: TabItem[] = [
    { key: 'photos', label: t('navigation.photos') },
    { key: 'events', label: t('navigation.events') },
  ];

  const staleTime = 15 * 1000;

  const { data, error, isPending, refetch, isRefetching, dataUpdatedAt } = useQuery({
    queryKey: ['discovery', 'overview', currentCommunity],
    queryFn: () => PhotosService.GetDiscoverLists(),
    enabled: !!currentCommunity && activeTab === 'photos',
    staleTime,
  });

  const {
    data: eventsData,
    error: eventsError,
    isPending: eventsIsPending,
    refetch: eventsRefetch,
    isRefetching: eventsIsRefetching,
    dataUpdatedAt: eventsDataUpdatedAt,
  } = useQuery({
    queryKey: ['discovery', 'events', currentCommunity],
    queryFn: () => EventsService.getDiscoverEvents(),
    enabled: !!currentCommunity && activeTab === 'events',
    staleTime,
  });

  const sections: PhotoSectionData[] = useMemo(
    () => [
      {
        key: 'latest',
        titleKey: 'photos.latest_short',
        subtitleKey: 'discover.sectionSubtitles.photos.latest',
        icon: Clock01Icon,
        photos: data?.LatestPhotos ?? [],
        route: '/photos/latest',
      },
      {
        key: 'network',
        titleKey: 'photos.network_short',
        subtitleKey: 'discover.sectionSubtitles.photos.network',
        icon: StarIcon,
        photos: data?.LatestPhotosFollowings ?? [],
        route: '/photos/network',
      },
      {
        key: 'private',
        titleKey: 'photos.private_short',
        subtitleKey: 'discover.sectionSubtitles.photos.private',
        icon: SquareLock01Icon,
        photos: data?.LatestPhotosPrivate ?? [],
        route: '/photos/private',
      },
      {
        key: 'favorites',
        titleKey: 'photos.favorites_short',
        subtitleKey: 'discover.sectionSubtitles.photos.favorites',
        icon: FavouriteIcon,
        photos: data?.LatestPhotosLiked ?? [],
        route: '/photos/favorites',
      },
    ],
    [data]
  );

  const eventSections: EventSectionData[] = useMemo(
    () => [
      {
        key: 'upcoming',
        titleKey: 'events.upcoming_short',
        subtitleKey: 'discover.sectionSubtitles.events.upcoming',
        icon: Calendar03Icon,
        events: eventsData?.UpcomingEvents ?? [],
        route: '/events/upcoming',
      },
      {
        key: 'nearby',
        titleKey: 'events.nearby_short',
        subtitleKey: 'discover.sectionSubtitles.events.nearby',
        subtitleParams: { distance: nearbyRadiusLabel },
        icon: Location01Icon,
        events: eventsData?.NearbyEvents ?? [],
        route: '/events/nearby',
      },
      {
        key: 'network',
        titleKey: 'events.network_short',
        subtitleKey: 'discover.sectionSubtitles.events.network',
        icon: UserGroupIcon,
        events: eventsData?.NetworkEvents ?? [],
        route: '/events/network',
      },
      {
        key: 'past',
        titleKey: 'events.past_short',
        subtitleKey: 'discover.sectionSubtitles.events.past',
        icon: Clock01Icon,
        events: eventsData?.PastEvents ?? [],
        route: '/events/past',
      },
    ],
    [eventsData, nearbyRadiusLabel]
  );

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      if (activeTab === 'photos') {
        await refetch();
      } else {
        await eventsRefetch();
      }
    } finally {
      setIsRefreshing(false);
    }
  };

  // Registers scroll to top for when the tab is pressed
  useScrollToTop(flashListRef);

  const isActiveTabRefetching = activeTab === 'photos' ? isRefetching : eventsIsRefetching;

  return (
    <>
      <ScreenHeader
        options={{
          hideBackButton: true,
          title: t('navigation.discover'),
          ...(Platform.OS === 'web' && {
            headerRight: () => (
              <HeaderRefreshButton
                isLargeScreen={isLargeScreen}
                isRefreshing={isRefreshing || isActiveTabRefetching}
                isDisabled={isActiveTabRefetching}
                inAppHeaderBar={!isLargeScreen}
                onPress={handleRefresh}
              />
            ),
          }),
        }}
      />

      <HStack className="min-h-12 items-center border-b border-background-200 dark:border-background-50">
        <InlineTabsBar
          tabs={tabs}
          activeTab={activeTab}
          onTabChange={handleTabChange}
          className="flex-1 border-b-0"
        />
        {activeTab !== 'events' && data?.IsMyTypeFiltered !== undefined && (
          <Box className="pr-4">
            <MyTypeFilterMenu isMyTypeFiltered={data.IsMyTypeFiltered} size="sm" />
          </Box>
        )}
      </HStack>

      {activeTab === 'photos' ? (
        error ? (
          <ErrorInListView error={error} onRetry={() => refetch()} isRetrying={isRefetching} />
        ) : (
          <FlashList
            ref={flashListRef}
            data={sections}
            renderItem={({ item }) => (
              <PhotoSection section={item} isLoading={isPending} photosPerRow={photosPerRow} />
            )}
            keyExtractor={(item) => item.key}
            contentContainerStyle={{ paddingHorizontal: 16, paddingVertical: 16 }}
            ListFooterComponent={DiscoverTabBarHint}
            refreshControl={<RefreshControl refreshing={isRefreshing} onRefresh={handleRefresh} />}
          />
        )
      ) : eventsError ? (
        <ErrorInListView
          error={eventsError}
          onRetry={() => eventsRefetch()}
          isRetrying={eventsIsRefetching}
        />
      ) : (
        <ScrollView
          contentContainerStyle={{ padding: 16 }}
          refreshControl={<RefreshControl refreshing={isRefreshing} onRefresh={handleRefresh} />}>
          {eventSections.map((section) => (
            <EventSection
              key={section.key}
              section={section}
              isLoading={eventsIsPending}
              cardWidth={eventCardWidth}
            />
          ))}
          <DiscoverTabBarHint />
        </ScrollView>
      )}
    </>
  );
}
